import React, { ChangeEvent, useEffect } from 'react'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
  useTheme,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { isNil } from 'lodash'
import { ControlledTextField } from '../../form/ControlledTextField'
import {
  BuildingPartCollectionFragment,
  DemandFragment,
  HourSeriesCollectionFragment,
  ProviderType,
  usePerformanceTablesQuery,
  useProjectTemperatureCurvesQuery,
  useTemperatureCurvesQuery,
} from '../../../generated/graphql'
import { FormValues } from './form-values'
import { FormGroup } from '../../form/FormGroup'
import { Dialog } from '../../dialog/Dialog'
import { validators } from '../../../validation/validators'
import { providerProperties } from '../../../provider/provider-properties'
import { useProviderNames } from '../../../provider/use-provider-names'
import { sortByProperty } from '../../../util/array-utility'

type Props = {
  isOpen: boolean
  defaultValues: Partial<FormValues>
  projectId: string
  providerTypes: ProviderType[]
  hourSeriesCollections: HourSeriesCollectionFragment[]
  demands: DemandFragment[]
  isFallbackProvider?: boolean
  buildingParts?: BuildingPartCollectionFragment[]
  currentProviderNames: string[]
  onClose: () => void
  onSubmit: (values: FormValues) => void
  onDelete?: () => void
}

export function ProviderDialog({
  isOpen,
  defaultValues,
  projectId,
  providerTypes,
  hourSeriesCollections,
  demands,
  isFallbackProvider,
  buildingParts,
  currentProviderNames,
  onClose,
  onSubmit,
  onDelete,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { typeToDefaultName } = useProviderNames(currentProviderNames)
  const theme = useTheme()
  const form = useForm<FormValues>({
    defaultValues,
  })
  const { reset, control } = form

  const selectedType = useWatch<ProviderType>({
    control,
    name: 'type',
  })
  const { data: performanceTablesData } = usePerformanceTablesQuery()
  const { data: temperatureCurvesData } = useTemperatureCurvesQuery()
  const { data: projectTemperatureCurveData } =
    useProjectTemperatureCurvesQuery({
      variables: { id: projectId },
    })

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  const onTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const type = event.target.value as ProviderType

    form.setValue('name', typeToDefaultName(type))
  }

  const isCreating = defaultValues.type === undefined
  const title = isCreating
    ? t('building.tabs.energySystem.addProvider')
    : defaultValues.name ?? ''

  const ParametersForm = selectedType
    ? providerProperties[selectedType].form
    : null

  const performanceTables = performanceTablesData?.performanceTables ?? []

  const projectSpecificTemperatureCurves = sortByProperty(
    projectTemperatureCurveData?.project.temperatureCurves ?? [],
    'name',
  )
  const globalTemperatureCurves = sortByProperty(
    temperatureCurvesData?.temperatureCurves ?? [],
    'name',
  )
  const temperatureCurves = [
    ...projectSpecificTemperatureCurves,
    ...globalTemperatureCurves,
  ]

  const patchHybridHeatPumpValues = (values: FormValues): FormValues => ({
    ...values,
    hourSeriesIds: [
      values.parameters?.hybridHeatPump?.wasteHeatSourceAHourSeriesId,
      values.parameters?.hybridHeatPump?.wasteHeatSourceBHourSeriesId,
    ].filter((id): id is string => !isNil(id)),
    performanceTableIds: [
      values.parameters?.hybridHeatPump?.performanceTableId,
    ].filter((id): id is string => !isNil(id)),
    temperatureCurveIds: [
      values.parameters?.hybridHeatPump?.heatingTemperatureCurveId,
      values.parameters?.hybridHeatPump?.coolingTemperatureCurveId,
      ...(values.parameters?.hybridHeatPump?.heatCircuits?.map(
        (heatCircuit) => heatCircuit.temperatureCurveId,
      ) ?? []),
    ].filter((id): id is string => !isNil(id)),
  })

  const validProviderTypes = providerTypes
    .filter((type) => Boolean(providerProperties[type].form))
    .filter(
      (type) => !isFallbackProvider || providerProperties[type].isUnrestricted,
    )

  const handleClose = () => {
    reset({})
    onClose()
  }

  return (
    <Dialog title={title} open={isOpen} onClose={handleClose}>
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit((values) => {
            if (selectedType !== ProviderType.HybridHeatPump) {
              onSubmit(values)
              return
            }

            onSubmit(patchHybridHeatPumpValues(values))
          })}
        >
          <DialogContent>
            <FormGroup>
              <ControlledTextField
                control={form.control}
                name="type"
                rules={validators.required}
                error={form.errors.type}
                label={t('createProvider.type')}
                onChange={onTypeChange}
                select
              >
                {validProviderTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {t(`providerType.${type}`)}
                  </MenuItem>
                ))}
              </ControlledTextField>
            </FormGroup>

            <FormGroup>
              <ControlledTextField
                control={form.control}
                name="name"
                rules={validators.required}
                error={form.errors.name}
                label={t('createProvider.name')}
                type="name"
                defaultValue={typeToDefaultName(selectedType)}
              />
            </FormGroup>

            {ParametersForm && (
              <>
                <Box my={5}>
                  <Divider />
                </Box>
                <ParametersForm
                  control={form.control}
                  errors={form.errors}
                  hourSeriesCollections={hourSeriesCollections}
                  demands={demands}
                  performanceTables={performanceTables}
                  temperatureCurves={temperatureCurves}
                  buildingParts={buildingParts ?? []}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
              <Box>
                {onDelete && !isFallbackProvider && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={onDelete}
                  >
                    {t('common.remove')}
                  </Button>
                )}
              </Box>

              <Box flexGrow={1} />

              <Button variant="contained" onClick={handleClose}>
                {t('common.cancel')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!selectedType}
              >
                {isCreating
                  ? t('building.tabs.energySystem.addProvider')
                  : t('common.saveChanges')}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
